export const CapabilityTier = {
  /**
   * @see {@link https://developer.atlassian.com/platform/slos/tome/capabilities/#tier-c1}
   * "A Capability classified as tier C1 means that the Capability has a publicly documented SLA which makes it a compensable experience."
   *
   * @see {@link https://www.atlassian.com/legal/sla} for a list of SLAs
   */
  C1: 'C1',
  /**
   * @see {@link https://developer.atlassian.com/platform/slos/tome/capabilities/#tier-c2}
   * "A Capability classified as tier C2 means that the Capability is a core offering of the product or platform, but is not a compensable experience. If the customer experience represented by the Capability is unavailable, it will adversely affect customer trust."
   */
  C2: 'C2',
  /**
   * @see {@link https://developer.atlassian.com/platform/slos/tome/capabilities/#tier-c3}
   * "A Capability classified as tier C3 means that the Capability is not a core offering of the product or platform, and represents functionality that is simply nice to have."
   */
  C3: 'C3',
  /**
   * Represents not having any capability tier specified at all.
   */
  NOT_SPECIFIED: 'not-specified',
  /**
   * Represents that whatever is being logged is not a capability
   */
  NONE: 'none',
} as const;

export type CapabilityTier = (typeof CapabilityTier)[keyof typeof CapabilityTier];

export const capabilityTierPriorities = [
  CapabilityTier.C1,
  CapabilityTier.C2,
  /**
   * Could be C1 or C2 but simply not specified
   */
  CapabilityTier.NOT_SPECIFIED,
  CapabilityTier.C3,
  CapabilityTier.NONE,
];

const tierToPriorityMap = new Map(capabilityTierPriorities.map((tier, i) => [tier, i]));
const priorityToTierMap = new Map(capabilityTierPriorities.map((tier, i) => [i, tier]));

export const tierToPriority = (tier: CapabilityTier) => {
  const priority = tierToPriorityMap.get(tier);
  if (priority === undefined) {
    throw new Error(`${tier} is an invalid tier`);
  }

  return priority;
};

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
export const priorityToTier = (priority: number) => {
  const tier = priorityToTierMap.get(priority);
  if (tier === undefined) {
    throw new Error(`${priority} is an invalid priority`);
  }

  return tier;
};

export const highestPriorityTier = (tiers: CapabilityTier[]) =>
  priorityToTier(Math.max(...tiers.map(tierToPriority)));

export const tiersThatCouldBeACapability = [
  CapabilityTier.C1,
  CapabilityTier.C2,
  CapabilityTier.C3,
  CapabilityTier.NOT_SPECIFIED,
];

export const CustomerImpact = {
  /**
   * Impacts real customers
   */
  CUSTOMERS: 'customers',
  /**
   * We're not sure what type of customer this would impact
   *
   * Ideally, this should be rarely used as it code should be written such that it's clear how an error would impact a customer
   */
  UNKNOWN: 'unknown',
  /**
   * Causes issues Atlassian. Specifically, more than just the Fabric team but Atlassians outside of Fabric
   *
   * Examples:
   * - FFs aren't working, causing delivery dates and rollouts being affected
   * - There's a bug in Whiteboards and we know it affects Atlassian customers
   */
  ATLASSIAN: 'atlassian',
  /**
   * Only causes issues for the fabric team.
   *
   * Examples:
   * - We can't use a certain ops or internal tool
   * - There's a bug in Whiteboards but we know it only affects the Fabric team (E.g. Maybe it's guaranteed to be dogfooded)
   */
  INTERNAL_TEAM: 'internal-team',
  /**
   * No impact on any user
   */
  NONE: 'none',
} as const;

export type CustomerImpact = (typeof CustomerImpact)[keyof typeof CustomerImpact];

/**
 * @returns true if the audience is larger than just the Fabric team (E.g.
 * unknown, Atlassian, customers, etc)
 */
export const audienceIsOutsideInternalTeam = (audience: CustomerImpact) =>
  audience !== CustomerImpact.INTERNAL_TEAM && audience !== CustomerImpact.NONE;

export const Actionability = {
  /**
   * We can guarantee there is an action to take for this event.
   */
  ACTIONABLE: 'actionable',
  /**
   * We do not know whether there is an action to take without investigation.
   */
  UNKNOWN: 'unknown',
  /**
   * We can guarantee that there is no action is needed for this event.
   *
   * Bad candidates:
   * - Logs that you can glean improvements
   *
   * Good candidates:
   * - Logging a user's internet disconnection. It's good to be aware it's
   *   happening but when our observability logic is working correctly, this is
   *   inherently something we have no control over. We're not going to fix the
   *   user's internet for them
   */
  NONE: 'none',
} as const;

export type Actionability = (typeof Actionability)[keyof typeof Actionability];

/**
 * Identifies the kind of canvas-content the log/metric/event originated from.
 *
 * Adding this single tag to {@link SharedMetadataTags} (and the equivalent
 * Splunk log fields and SignalFx dimensions) means **every** Sentry issue,
 * Splunk log row, and SignalFx metric automatically becomes filterable by
 * content type — unblocking slides-specific dashboards, SLOs, and detectors
 * with a single change.
 *
 * Source: Slides Observability Gap Analysis (action item C1, P1).
 */
export const ContentType = {
  /**
   * A Confluence Whiteboard document.
   */
  WHITEBOARD: 'whiteboard',
  /**
   * A Confluence Slide / AI-generated deck.
   */
  SLIDE: 'slide',
  /**
   * A Confluence Database document.
   */
  DATABASE: 'database',
  /**
   * The content type is genuinely unknown / not applicable to the surface
   * emitting the event (e.g. anonymous infrastructure-level errors).
   */
  UNKNOWN: 'unknown',
} as const;

export type ContentType = (typeof ContentType)[keyof typeof ContentType];

/**
 * This serves to help us filter out less-important Sentry errors by classifying whether a given log call is supposed to _guarantee_ that a bug has ocurred in our app
 * or whether we're logging it out because we think there's a bug but there might be errors mixed in that are not bugs.
 *
 * Note that this field alone doesn't help filter for severity. For that, see {@link PLACEHOLDER}
 *
 * @see https://bitbucket.org/atlassian/canvas/pull-requests/13583/overview#comment-508996811 for a little more context
 */
export const EventClassification = {
  /**
   * For when the code that's only meant to ever executed when a bug has ocurred.
   *
   * Note: Choosing a probability-of-bug value should not be speculate/account for specific bugs in the code base
   *
   * Examples:
   * - When you have a try-catch and you KNOW that the things that will be thrown are under circumstances where there must be some sort of bug somewhere in our (or someone else's) code base
   * - HTTP 500 status code handling logic (assuming 500s only happen when the server has experienced a bug/crash)
   * - Server responses that signal that the JSON payload frontend sent was completely wrong
   * - Part of your app unexpectedly crashes
   * - Any response bodys/HTTP statuses you were not expecting to receive
   */
  BUG: 'bug',
  /**
   * For when the code ambiguously handles cases where a bug has or hasn't occurred.
   * Generally speaking, it's recommended to try avoid writing code that results in using this value as it makes filtering in Sentry more ambiguous.
   *
   * Examples:
   * - Logging for expected but also unexpected HTTP error responses with exactly the same code such as 429s but also 5XXs via the same log call
   * - Logging for user input validation errors but also crash errors via the same log call
   * - catch-all/catch-rest cases in a catch { } clause When you have no idea what you're actually going to be catching in a try catch and you think it might execute under non-failure-mode/crash handling circumstances
   *
   * Note: The catch-all / catch-rest case in a `catch (error) { }` block can
   * **never** use `EventClassification.LOG`. Because you cannot control what is
   * thrown into that block, it is either:
   * - `EventClassification.BUG` — if the code was never expected to enter the
   *   catch block at all (i.e. any throw is a bug), or
   * - `EventClassification.UNKNOWN` — if the block handles a mix of expected
   *   noise and genuine bugs that you cannot distinguish at the call site.
   */
  UNKNOWN: 'unknown',
  /**
   * For when the code is meant to only ever execute when no bug has ocurred.
   *
   * Examples:
   * - 'Happy path' code but you've decided to log out to Sentry anyway for whatever reason
   * - HTTP 200 status code handling logic (assuming 200s only happen when the server is healthy) that you've decided to log out
   * - User validation errors that you've decided to log out
   * - Any response body's/HTTP statuses you were expecting to receive that do not signify that the server is experienced a bug (E.g. 2XXs)
   *
   * BAD candidates for EventClassification.LOG:
   * - The catch-all / catch-rest in a `catch (error) { }`. Fundamentally you
   *   have no idea what's going to be thrown so you cannot just assume
   *   everything being caught is just a log
   */
  LOG: 'log',
} as const;

export type EventClassification =
  (typeof EventClassification)[keyof typeof EventClassification];
